/**
 * Documentación para partners (CRMs como HermandApp, PlayOff, AMPAnet…).
 *
 * Hero editorial + tabla de verticales + embed widget + webhooks + tabs
 * Python/Node/PHP para verificar firma HMAC-SHA256.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import TopBar from '../components/TopBar';

const EVENTS = [
  { name: 'share.paid', desc: 'Un miembro completó el pago de su share' },
  { name: 'group_buy.completed', desc: 'El cobro alcanzó el nº esperado de miembros' },
  { name: 'group_buy.expired', desc: 'Pasó el deadline sin completarse' },
  { name: 'group_buy.settled', desc: 'Transfer Stripe Connect ejecutado al organizador' },
  { name: 'group_buy.cancelled', desc: 'Organizador canceló el cobro' },
  { name: 'share.refunded', desc: 'Se reembolsó un share concreto' },
  { name: 'porra.result_published', desc: 'Resultado de la porra cargado · ganador identificado' },
  { name: 'raffle.drawn', desc: 'Sorteo verificable ejecutado HMAC-SHA256' },
];

const VERTICALS = [
  { type: 'raffle_share', label: 'Sorteo de peña con papeleta nominativa', fee: '2,5 %' },
  { type: 'papeleta_sitio', label: 'Papeleta de sitio de hermandad', fee: '2,0 %' },
  { type: 'ticket_share', label: 'Entradas en grupo', fee: '2,0 %' },
  { type: 'membership_quota', label: 'Cuotas de asociación', fee: '1,5 %' },
  { type: 'group_gift', label: 'Regalo conjunto', fee: '3,0 %' },
  { type: 'event_crowdfund', label: 'Crowdfunding micro de evento', fee: '5,0 %' },
  { type: 'porra', label: 'Porras · quinielas · predicciones', fee: '3,0 %' },
];

const PYTHON_EXAMPLE = `import hmac
import hashlib

def verify_signature(body: bytes, header_signature: str, secret: str) -> bool:
    expected = hmac.new(
        secret.encode('utf-8'),
        body,
        hashlib.sha256,
    ).hexdigest()
    return hmac.compare_digest(expected, header_signature)

# FastAPI:
@app.post('/webhooks/pagafacil')
async def cc_webhook(request: Request):
    body = await request.body()
    sig = request.headers.get('X-CobroColectivo-Signature', '')
    if not verify_signature(body, sig, MY_SECRET):
        raise HTTPException(401, 'invalid signature')

    event = request.headers.get('X-CobroColectivo-Event')
    payload = json.loads(body)
    # … actualiza tu CRM
    return {'ok': True}`;

const NODE_EXAMPLE = `const crypto = require('crypto');

function verifySignature(body, headerSignature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(body)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected, 'hex'),
    Buffer.from(headerSignature, 'hex'),
  );
}

app.post('/webhooks/pagafacil',
  express.raw({ type: 'application/json' }),
  (req, res) => {
    const sig = req.headers['x-cobrocolectivo-signature'];
    if (!verifySignature(req.body, sig, MY_SECRET)) {
      return res.status(401).end();
    }
    const event = req.headers['x-cobrocolectivo-event'];
    const payload = JSON.parse(req.body);
    // … actualiza tu CRM
    res.json({ ok: true });
  });`;

const PHP_EXAMPLE = `<?php
function verifySignature($body, $headerSignature, $secret): bool {
    $expected = hash_hmac('sha256', $body, $secret);
    return hash_equals($expected, $headerSignature);
}

$body = file_get_contents('php://input');
$sig = $_SERVER['HTTP_X_COBROCOLECTIVO_SIGNATURE'] ?? '';
if (!verifySignature($body, $sig, MY_SECRET)) {
    http_response_code(401);
    exit;
}
$event = $_SERVER['HTTP_X_COBROCOLECTIVO_EVENT'] ?? '';
$payload = json_decode($body, true);
// … actualiza tu CRM
echo json_encode(['ok' => true]);`;

const CURL_EXAMPLE = `curl -X POST https://pagafacil.eu/api/public/group-buys \\
  -H 'Content-Type: application/json' \\
  -d '{
    "type": "raffle_share",
    "title": "Sorteo Peña Pajeros",
    "organizer_name": "Pepe García",
    "organizer_email": "pepe@penalospajeros.com",
    "amount_per_share": 5.00,
    "expected_shares": 20,
    "partner_webhook_url": "https://tucrm.com/webhooks/pagafacil"
  }'`;

const PAYLOAD_EXAMPLE = `{
  "event": "share.paid",
  "occurred_at": "2026-06-12T18:00:00Z",
  "group_buy": {
    "id": "9c6a…",
    "slug": "pena-pajeros-rocio-2026",
    "type": "raffle_share",
    "title": "Peña Los Pajeros — Sorteo Rocío 2026",
    "status": "open",
    "paid_shares_count": 12,
    "expected_shares": 20,
    "total_paid_amount": 60.00,
    "organizer_email": "presi@penalospajeros.com",
    "partner_name": "HermandApp"
  },
  "share": {
    "order_reference": "GB-A1B2C3",
    "buyer_name": "Pepe García",
    "buyer_email": "pepe@email.com",
    "quantity": 1,
    "total_amount": 5.00
  }
}`;

const EMBED_HTML = `<!-- En la ficha del cobro de vuestro CRM -->
<iframe
  src="https://pagafacil.eu/embed/g/SLUG"
  width="400"
  height="600"
  style="border:0;border-radius:12px"
  loading="lazy">
</iframe>

<script>
window.addEventListener('message', (e) => {
  if (e.origin !== 'https://pagafacil.eu') return;
  if (e.data.type === 'cc:embed:join') {
    console.log('Join:', e.data.order_id, e.data.reference);
  }
});
</script>`;

const EMBED_REACT = `import { useEffect } from 'react';

export function CobroColectivoEmbed({ slug }) {
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== 'https://pagafacil.eu') return;
      if (e.data.type === 'cc:embed:join') {
        // refresca tu UI
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  return (
    <iframe
      title="Pagafácil"
      src={\`https://pagafacil.eu/embed/g/\${slug}\`}
      width={400}
      height={600}
      style={{ border: 0, borderRadius: 12 }}
      loading="lazy"
    />
  );
}`;


export default function PartnerDocsPage() {
  const [sigTab, setSigTab] = useState<'python' | 'node' | 'php'>('python');
  const [embedTab, setEmbedTab] = useState<'html' | 'react' | 'curl'>('html');

  const sigCode =
    sigTab === 'python' ? PYTHON_EXAMPLE : sigTab === 'node' ? NODE_EXAMPLE : PHP_EXAMPLE;
  const embedCode =
    embedTab === 'html' ? EMBED_HTML : embedTab === 'react' ? EMBED_REACT : CURL_EXAMPLE;

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <TopBar contextLabel="Partners CRM" rightCta={{ label: 'partners@pagafacil.eu', to: 'mailto:partners@pagafacil.eu' }} />

      {/* Hero */}
      <section>
        <div className="max-w-5xl mx-auto px-6 pt-16 md:pt-20 pb-14">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-4">
            · Integración para partners
          </div>
          <h1 className="font-display font-bold text-[44px] sm:text-[54px] lg:text-[60px] leading-[1.0] tracking-[-0.02em] text-ink mb-5 max-w-[820px]">
            Conecta tu CRM al motor de pagafácil.
          </h1>
          <p className="text-[17px] leading-relaxed text-ink-700 max-w-[640px] mb-6">
            Vuestro CRM ya gestiona miembros, cuotas y comunicación. Lo que NO tenéis:
            pagafácil con pago individual, papeleta nominativa con DNI, reserva
            atómica y sorteo verificable. Eso lo ponemos nosotros.
          </p>
          <p className="text-[14.5px] text-ink-700 max-w-[640px]">
            <strong className="text-ink">Integración en 30 minutos.</strong>{' '}
            Embed widget en iframe + webhook saliente firmado con HMAC-SHA256.
          </p>
        </div>
      </section>

      {/* 7 verticales */}
      <section className="bg-paper border-y border-line">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · 7 verticales sobre el mismo motor
          </div>
          <h2 className="font-display font-bold text-[30px] md:text-[36px] leading-[1.05] tracking-[-0.02em] text-ink mb-8 max-w-[560px]">
            Una sola API. Muchas formas de cobrar.
          </h2>
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 border-b border-line">
                  <th className="text-left py-3 font-normal">type</th>
                  <th className="text-left py-3 font-normal">Uso</th>
                  <th className="text-right py-3 font-normal">Comisión</th>
                </tr>
              </thead>
              <tbody>
                {VERTICALS.map((v) => (
                  <tr key={v.type} className="border-b border-line-soft">
                    <td className="py-3 font-mono text-brand-700">{v.type}</td>
                    <td className="py-3 text-ink-700">{v.label}</td>
                    <td className="py-3 text-right font-display font-semibold text-ink tabular-nums">
                      {v.fee}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Embed */}
      <section>
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · 1 · Embed widget
          </div>
          <h2 className="font-display font-bold text-[30px] md:text-[36px] leading-[1.05] tracking-[-0.02em] text-ink mb-3 max-w-[560px]">
            El formulario de pago, dentro de tu CRM.
          </h2>
          <p className="text-[14.5px] text-ink-700 mb-6 max-w-[640px]">
            Mete el iframe en cualquier ficha. Nos comunicamos contigo por{' '}
            <code className="font-mono text-[13px] bg-line-soft px-1.5 py-0.5 rounded">
              postMessage
            </code>{' '}
            cuando un miembro completa el pago.
          </p>
          <Tabs
            tabs={[
              { id: 'html', label: 'HTML' },
              { id: 'react', label: 'React' },
              { id: 'curl', label: 'cURL · crear cobro' },
            ]}
            value={embedTab}
            onChange={(v) => setEmbedTab(v as any)}
          />
          <CodeBlock code={embedCode} />
        </div>
      </section>

      {/* Webhooks */}
      <section className="bg-paper border-y border-line">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · 2 · Webhooks salientes
          </div>
          <h2 className="font-display font-bold text-[30px] md:text-[36px] leading-[1.05] tracking-[-0.02em] text-ink mb-3 max-w-[640px]">
            Avisos automáticos a tu CRM.
          </h2>
          <p className="text-[14.5px] text-ink-700 mb-6 max-w-[640px]">
            Al crear el cobro, pasas tu{' '}
            <code className="font-mono text-[13px] bg-line-soft px-1.5 py-0.5 rounded">
              partner_webhook_url
            </code>
            . Cuando ocurre un evento, hacemos POST con HMAC-SHA256 firmado con tu
            secret.
          </p>
          <div className="overflow-x-auto -mx-6 px-6 mb-8">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 border-b border-line">
                  <th className="text-left py-3 font-normal">event</th>
                  <th className="text-left py-3 font-normal">Cuándo</th>
                </tr>
              </thead>
              <tbody>
                {EVENTS.map((e) => (
                  <tr key={e.name} className="border-b border-line-soft">
                    <td className="py-3 font-mono text-brand-700 whitespace-nowrap pr-4">
                      {e.name}
                    </td>
                    <td className="py-3 text-ink-700">{e.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="font-display font-semibold text-[20px] text-ink tracking-[-0.01em] mb-3">
            Payload de ejemplo · share.paid
          </h3>
          <CodeBlock code={PAYLOAD_EXAMPLE} />
        </div>
      </section>

      {/* Verificación de firma */}
      <section>
        <div className="max-w-5xl mx-auto px-6 py-14">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · 3 · Verificación de firma
          </div>
          <h2 className="font-display font-bold text-[30px] md:text-[36px] leading-[1.05] tracking-[-0.02em] text-ink mb-3 max-w-[640px]">
            HMAC-SHA256 · siempre.
          </h2>
          <p className="text-[14.5px] text-ink-700 mb-6 max-w-[640px]">
            Verifica la firma del header{' '}
            <code className="font-mono text-[13px] bg-line-soft px-1.5 py-0.5 rounded">
              X-CobroColectivo-Signature
            </code>{' '}
            antes de procesar el payload.
          </p>
          <Tabs
            tabs={[
              { id: 'python', label: 'Python' },
              { id: 'node', label: 'Node.js' },
              { id: 'php', label: 'PHP' },
            ]}
            value={sigTab}
            onChange={(v) => setSigTab(v as any)}
          />
          <CodeBlock code={sigCode} />

          <h3 className="font-display font-semibold text-[20px] text-ink tracking-[-0.01em] mt-10 mb-3">
            Headers que enviamos
          </h3>
          <div className="bg-paper border border-line rounded-card overflow-hidden">
            <table className="w-full text-[14px]">
              <tbody>
                <Header
                  name="X-CobroColectivo-Event"
                  desc={
                    <>
                      Nombre del evento · ej.{' '}
                      <code className="font-mono text-[12.5px]">share.paid</code>
                    </>
                  }
                />
                <Header
                  name="X-CobroColectivo-Signature"
                  desc="HMAC-SHA256 hex del body firmado con tu secret"
                />
                <Header
                  name="X-CobroColectivo-Delivery-Id"
                  desc="ID único por entrega · úsalo para idempotencia"
                />
                <Header
                  name="User-Agent"
                  desc={
                    <code className="font-mono text-[12.5px]">CobroColectivo-Webhook/1.0</code>
                  }
                  last
                />
              </tbody>
            </table>
          </div>
          <p className="text-[12.5px] text-ink-500 mt-4">
            Reintentamos hasta 3 veces con backoff exponencial (1 s, 4 s, 16 s) si
            devuelves 5xx o timeout. Devuelve 2xx para considerar la entrega exitosa.
          </p>
        </div>
      </section>

      {/* Empezar en 5 pasos */}
      <section className="bg-albero border-t border-line-strong">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Empezar en 5 pasos
          </div>
          <h2 className="font-display font-bold text-[30px] md:text-[36px] leading-[1.05] tracking-[-0.02em] text-ink mb-10 max-w-[560px]">
            Cinco pasos. Ni uno más.
          </h2>
          <ol className="grid md:grid-cols-2 gap-x-10 gap-y-7">
            {[
              {
                t: 'Crea un cobro de prueba',
                d: (
                  <>
                    Pasa tu{' '}
                    <code className="font-mono text-[12.5px] bg-line-soft px-1 py-0.5 rounded">
                      partner_webhook_url
                    </code>
                    .
                  </>
                ),
              },
              {
                t: 'Recibes tu secret',
                d: (
                  <>
                    En la respuesta llega{' '}
                    <code className="font-mono text-[12.5px] bg-line-soft px-1 py-0.5 rounded">
                      partner_webhook_secret
                    </code>
                    . Guárdalo.
                  </>
                ),
              },
              {
                t: 'Embed widget en tu ficha',
                d: <>Iframe de 400×600. Listo en 30 segundos.</>,
              },
              {
                t: 'Un miembro paga 1 cent (test)',
                d: <>Con Stripe en test mode. Sin gastos reales.</>,
              },
              {
                t: 'Webhook llega a tu URL',
                d: (
                  <>
                    Recibes{' '}
                    <code className="font-mono text-[12.5px] bg-line-soft px-1 py-0.5 rounded">
                      share.paid
                    </code>{' '}
                    · verifica firma · actualiza CRM.
                  </>
                ),
              },
            ].map((s, i) => (
              <li key={i} className="border-t border-line-strong pt-5">
                <div className="font-display font-bold text-[34px] leading-none text-brand mb-2">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="font-display font-semibold text-[20px] text-ink tracking-[-0.01em] mb-1">
                  {s.t}
                </h3>
                <div className="text-[14px] text-ink-700 leading-relaxed">{s.d}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-paper">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20 text-center">
          <h2 className="font-display font-bold text-[34px] md:text-[44px] leading-[1.05] tracking-[-0.02em] mb-4">
            ¿Revenue share o integración a medida?
          </h2>
          <p className="text-[15.5px] text-paper/85 mb-8 max-w-[520px] mx-auto">
            Escríbenos y te respondemos en menos de 24 h en hora ibérica.
          </p>
          <a
            href="mailto:partners@cobrocolectivo.es"
            className="inline-block px-9 py-4 rounded-xl bg-paper text-brand font-semibold text-[15px] hover:bg-line transition shadow-card"
          >
            partners@cobrocolectivo.es
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}


function Tabs({
  tabs,
  value,
  onChange,
}: {
  tabs: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex gap-2 mb-3 flex-wrap">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={
            value === t.id
              ? 'px-4 py-1.5 rounded-pill text-[12.5px] font-semibold border-[1.5px] border-brand bg-brand-50 text-brand-700'
              : 'px-4 py-1.5 rounded-pill text-[12.5px] font-semibold border-[1.5px] border-line-strong bg-paper text-ink-700 hover:border-brand transition'
          }
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}


function CodeBlock({ code }: { code: string }) {
  return (
    <pre className="bg-ink text-[#F4ECDF] rounded-card p-5 text-[12.5px] leading-[1.6] overflow-x-auto font-mono">
      <code>{code}</code>
    </pre>
  );
}


function Header({
  name,
  desc,
  last,
}: {
  name: string;
  desc: React.ReactNode;
  last?: boolean;
}) {
  return (
    <tr className={last ? '' : 'border-b border-line-soft'}>
      <td className="px-5 py-3 font-mono text-brand-700 align-top whitespace-nowrap">
        {name}
      </td>
      <td className="px-5 py-3 text-ink-700">{desc}</td>
    </tr>
  );
}


function Mark({ size = 22 }: { size?: number }) {
  return (
    <div
      className="rounded-full"
      style={{
        width: size,
        height: size,
        background: 'conic-gradient(#1C5D82 0 33%, #C2683E 0 66%, #5E7B3E 0 100%)',
        boxShadow: 'inset 0 0 0 4px #FFFFFF',
      }}
      aria-hidden
    />
  );
}
